import React from 'react';
import Icon from '../../../components/AppIcon';

import { Checkbox } from '../../../components/ui/Checkbox';

const SessionTimeoutControls = ({ preferences, onUpdate }) => {
  const timeoutOptions = [
    { value: 15, label: '15 minutes', description: 'Short timeout for high security' },
    { value: 30, label: '30 minutes', description: 'Balanced timeout (recommended)' },
    { value: 60, label: '60 minutes', description: 'Extended timeout for convenience' },
  ];

  const refreshIntervalOptions = [
    { value: 5, label: '5 minutes', description: 'Frequent refresh for maximum security' },
    { value: 15, label: '15 minutes', description: 'Standard refresh interval (recommended)' },
    { value: 30, label: '30 minutes', description: 'Less frequent refresh' },
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Clock" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">Session Timeout Protection</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure automatic logout after periods of inactivity to protect your account
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Enable Session Timeout */}
        <div>
          <Checkbox
            checked={preferences?.sessionTimeout?.enabled}
            onChange={(checked) => onUpdate('sessionTimeout', { enabled: checked })}
            label="Enable Session Timeout"
            description="Automatically track inactivity and enforce session expiration"
          />
        </div>

        {/* Timeout Duration */}
        {preferences?.sessionTimeout?.enabled && (
          <>
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Inactivity Timeout Duration</label>
              {timeoutOptions?.map((option) => (
                <Checkbox
                  key={option?.value}
                  checked={preferences?.sessionTimeout?.duration === option?.value}
                  onChange={() => onUpdate('sessionTimeout', { duration: option?.value })}
                  label={option?.label}
                  description={option?.description}
                />
              ))}
            </div>

            {/* Auto Logout */}
            <div>
              <Checkbox
                checked={preferences?.sessionTimeout?.autoLogout}
                onChange={(checked) => onUpdate('sessionTimeout', { autoLogout: checked })}
                label="Auto-Logout on Timeout"
                description="Automatically sign out when session timeout is reached"
              />
            </div>
          </>
        )}

        {/* Token Refresh Settings */}
        <div className="pt-6 border-t border-border">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="RefreshCw" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-foreground">Token Refresh</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically refresh authentication tokens to maintain valid sessions
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Checkbox
              checked={preferences?.tokenRefresh?.enabled}
              onChange={(checked) => onUpdate('tokenRefresh', { enabled: checked })}
              label="Enable Automatic Token Refresh"
              description="Keep your session active by refreshing tokens periodically"
            />

            {preferences?.tokenRefresh?.enabled && (
              <div className="space-y-3 ml-6">
                <label className="text-sm font-medium text-foreground">Refresh Interval</label>
                {refreshIntervalOptions?.map((option) => (
                  <Checkbox
                    key={option?.value}
                    checked={preferences?.tokenRefresh?.interval === option?.value}
                    onChange={() => onUpdate('tokenRefresh', { interval: option?.value })}
                    label={option?.label}
                    description={option?.description}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">Security Recommendation</p>
            <p>
              For maximum security, enable session timeout with a 15-30 minute duration and automatic token refresh.
              This ensures your account is protected from unauthorized access while maintaining a seamless experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutControls;